import React, { useEffect } from 'react';
import { Box, ListItemIcon, ListSubheader, MenuItem } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { readToken, sbg } from '../../theme/sbgTokens';

/*
 * Light / Dark / System, shared by the account menu and the command palette.
 * The theme is created with `colorSchemeSelector: 'data-theme'`, so MUI's
 * useColorScheme() writes the choice to localStorage ('mui-mode') and to
 * <html data-theme>; public/index.html reads the same key before React boots.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

export const THEME_MODES: { value: ThemeMode; label: string; icon: LucideIcon }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/** The current mode (light until the provider reports one) and a setter. */
export const useThemeMode = () => {
  const { mode, setMode } = useColorScheme();
  return { mode: (mode ?? 'light') as ThemeMode, setMode: setMode as (next: ThemeMode) => void };
};

/**
 * Keeps <meta name="theme-color"> (mobile browser chrome) on the top bar's
 * surface color as the resolved scheme changes — a menu or palette switch,
 * or the OS flipping while in System mode. Renders nothing.
 */
export const ThemeColorSync: React.FC = () => {
  const { colorScheme } = useColorScheme();
  useEffect(() => {
    // Runs after MUI's layout effect has moved <html data-theme>, so the token is current.
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const surface = readToken('bg-surface');
    if (meta && surface) meta.content = surface;
  }, [colorScheme]);
  return null;
};

/**
 * The Theme group for a MUI Menu: an overline label and three
 * `menuitemradio` rows with a trailing check on the current mode.
 * Returned as an array (not a component) so MenuList keeps its roving focus.
 */
export const renderThemeModeItems = (mode: ThemeMode, onSelect: (mode: ThemeMode) => void) => [
  <ListSubheader key="theme-label" role="none" disableSticky sx={{ pt: 1 }}>
    Theme
  </ListSubheader>,
  ...THEME_MODES.map(({ value, label, icon: Icon }) => {
    const checked = mode === value;
    return (
      <MenuItem
        key={`theme-${value}`}
        role="menuitemradio"
        aria-checked={checked}
        aria-label={`${label} theme`}
        onClick={() => onSelect(value)}
      >
        <ListItemIcon>
          <Icon size={16} aria-hidden="true" />
        </ListItemIcon>
        <Box component="span" sx={{ flex: 1 }}>
          {label}
        </Box>
        {checked && (
          <Box component="span" sx={{ display: 'inline-flex', color: sbg.text.accent }}>
            <Check size={16} aria-hidden="true" />
          </Box>
        )}
      </MenuItem>
    );
  }),
];
